import moment from 'moment';

export function formatNumber(value: any) {
  return Number(value).toLocaleString();
}

/**
 * Converts ip address integer to 32 bit IPv4
 *
 * @param {number} upInt
 * @return {string} 32 bit IPv4
 */
export function intToIP(ipInt: number) {
  // eslint-disable-next-line no-bitwise,no-mixed-operators
  return `${ipInt >>> 24}.${ipInt >> 16 & 255}.${ipInt >> 8 & 255}.${ipInt & 255}`;
}

/**
 * Converts numbers to US currency format
 *
 * @param {string|number} price Price in cents
 * @param {bool} longForm Whether or not to include cents if whole dollar amount
 * @return {string} Formatted price, with dollar sign
 */
export function formatCurrency(price: number|string, longForm = false) {
  const numPrice = typeof price === 'string'
    ? parseInt(price, 10)
    : price;
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  if (longForm) {
    return formatter.format(numPrice / 100);
  }
  // Remove cents if '.00'
  return formatter.format(numPrice / 100).replace(/\D00$/, '');
}

/**
 * Remove duplicates from string arrays (up to two dimensions)
 * E.g. ['test', 'test2', 'test', ['test', 'test3'], ['test3', 'test']]
 * => ['test', 'test2', ['test', 'test3']]
 * @param {array} stringArray Array of: strings or array of strings
 * @return {array} Array with duplicates removed
 */
export const removeDuplicatesFromStringArray = (stringArray: Array<string|Array<string>>) => {
  const sortedArray = stringArray.map((facet) => {
    if (Array.isArray(facet)) {
      return facet.sort();
    }
    return facet;
  });
  return [
    ...new Set(
      sortedArray.map((el) => JSON.stringify(el)),
    ),
  ].map((el) => JSON.parse(el));
};

/**
 * Merges overlapping sets of Algolia facet filters, based on attribute name
 * E.g.
 * [
 *     "test:test1",
 *     "test3:test2",
 *     [
 *         "test:test1",
 *         "test:test2"
 *     ],
 *     [
 *         "test2:test1",
 *         "test2:test2",
 *         "test2:test3"
 *     ],
 *     [
 *         "test2:test1",
 *         "test2:test3"
 *     ]
 * ]
 * => [
 *     [
 *         "test:test1",
 *         "test:test2"
 *     ],
 *     "test3:test2",
 *     [
 *         "test2:test1",
 *         "test2:test2",
 *         "test2:test3"
 *     ]
 * ]
 * @param facetFilters
 */
export const mergeOverlappingFacets = (facetFilters: Array<string|Array<string>>) => {
  const facets: {
    [key: string]: Array<string|Array<string>>,
  } = {};
  facetFilters.forEach((facetSet) => {
    const attribute = typeof facetSet === 'string'
      ? facetSet.split(':')[0]
      : facetSet[0].split(':')[0];

    if (facets[attribute]) {
      facets[attribute].push(facetSet);
    } else {
      facets[attribute] = [facetSet];
    }
  });
  return Object.values(facets)
    .map((set) => {
      const simplifiedSet = removeDuplicatesFromStringArray(set.flat(Infinity));
      // TODO: Consider looking for default facets that could work as arrays,
      //  but are currently passed as simple strings; cf. https://schweb.atlassian.net/browse/LPBUGS-1356
      if (simplifiedSet.length === 1) {
        return simplifiedSet[0];
      }
      return simplifiedSet;
    });
};
/**
 * Parses a numericFilters array and returns an object
 * that contains the following human-readable values:
 * - minPrice
 * - maxPrice
 * - age
 *
 * @param numericFilters
 */
export const parsePriceAndAgeFromNumericFilters = (numericFilters: (string | string[])[]) => {
  let price = '';
  let age = '';
  if (numericFilters.length > 0) {
    numericFilters.forEach((numFilter) => {
      if (typeof numFilter === 'string') {
        if (numFilter.includes('real_price')) {
          price = numFilter;
        }
        if (numFilter.includes('birth_date')) {
          age = numFilter;
        }
      }
    });
  }

  let minPrice = '';
  let maxPrice = '';
  if (price) {
    if (price.includes('>=')) {
      [, minPrice] = price.split(' >= ');
    } else if (price.includes('<=')) {
      [, maxPrice] = price.split(' <= ');
    } else if (price.includes(':')) {
      [minPrice, maxPrice] = price.replace('real_price:', '').split(' TO ');
    }
  }
  let diffInMonths = 0;
  if (age && age.includes(':')) {
    const [minAge, maxAge] = age.replace('birth_date:', '')
      .split(' TO ')
      .map((dateString) => parseInt(dateString, 10));
    diffInMonths = Math.round(
      moment.unix(maxAge)
        .diff(
          moment.unix(minAge),
          'months',
          true,
        ),
    );
    if (diffInMonths > 12) {
      diffInMonths = 1;
    }
  }
  return {
    minPrice,
    maxPrice,
    diffInMonths,
  };
};
